import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Activity,
  AlertCircle,
  CheckCircle,
  Database,
  HardDrive,
  Mail,
  MessageSquare,
  Loader2,
  RefreshCw,
  Server,
  Zap,
  TrendingUp,
  Clock,
  AlertTriangle,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface HealthMetric {
  name: string
  status: 'healthy' | 'warning' | 'error' | 'unknown'
  message: string
  value?: string | number
  threshold?: string | number
  percentage?: number
  lastChecked: string
  icon: React.ElementType
}

interface SystemHealthProps {
  user: AdminUser | null
}

export default function SystemHealth({ user }: SystemHealthProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [healthMetrics, setHealthMetrics] = useState<HealthMetric[]>([])
  const [overallStatus, setOverallStatus] = useState<'healthy' | 'warning' | 'error'>('healthy')
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  // Check if user is super admin
  if (user?.adminLevel !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="w-96">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access system health monitoring.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  useEffect(() => {
    checkSystemHealth()
    // Auto-refresh every 30 seconds
    const interval = setInterval(checkSystemHealth, 30000)
    return () => clearInterval(interval)
  }, [])

  async function checkSystemHealth() {
    try {
      setRefreshing(true)
      const metrics: HealthMetric[] = []

      // 1. Database Health Check
      const dbHealth = await checkDatabaseHealth()
      metrics.push(dbHealth)

      // 2. SMS Credits Check
      const smsHealth = await checkSMSCredits()
      metrics.push(smsHealth)

      // 3. Storage Usage Check
      const storageHealth = await checkStorageUsage()
      metrics.push(storageHealth)

      // 4. Edge Functions Health
      const edgeFunctionsHealth = await checkEdgeFunctions()
      metrics.push(edgeFunctionsHealth)

      // 5. API Response Time Check
      const apiHealth = await checkAPIResponseTime()
      metrics.push(apiHealth)

      // 6. Active Users Check
      const usersHealth = await checkActiveUsers()
      metrics.push(usersHealth)

      setHealthMetrics(metrics)

      // Determine overall status
      const hasError = metrics.some(m => m.status === 'error')
      const hasWarning = metrics.some(m => m.status === 'warning')

      if (hasError) {
        setOverallStatus('error')
      } else if (hasWarning) {
        setOverallStatus('warning')
      } else {
        setOverallStatus('healthy')
      }

      setLastRefresh(new Date())
    } catch (error) {
      console.error('Error checking system health:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  async function checkDatabaseHealth(): Promise<HealthMetric> {
    try {
      const startTime = Date.now()
      const { data, error } = await supabase
        .from('churches')
        .select('id')
        .limit(1)
      const responseTime = Date.now() - startTime

      if (error) throw error

      return {
        name: 'Database',
        status: responseTime < 100 ? 'healthy' : responseTime < 500 ? 'warning' : 'error',
        message: responseTime < 100 ? 'Responding normally' : responseTime < 500 ? 'Slow response' : 'Very slow response',
        value: `${responseTime}ms`,
        threshold: '< 100ms optimal',
        lastChecked: new Date().toISOString(),
        icon: Database,
      }
    } catch (error) {
      return {
        name: 'Database',
        status: 'error',
        message: 'Connection failed',
        value: 'Offline',
        lastChecked: new Date().toISOString(),
        icon: Database,
      }
    }
  }

  async function checkSMSCredits(): Promise<HealthMetric> {
    try {
      // Check mNotify credits via edge function or stored value
      // For now, we'll read from a settings table or environment
      // TODO: Call mNotify API to get actual balance

      // Placeholder: Read from last known balance
      const { data: settings } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'sms_credits_balance')
        .single()

      const credits = settings?.value || 899 // Default to last known value

      let status: 'healthy' | 'warning' | 'error' = 'healthy'
      let message = 'Sufficient credits'

      if (credits < 50) {
        status = 'error'
        message = 'Critical: Very low credits!'
      } else if (credits < 200) {
        status = 'warning'
        message = 'Warning: Credits running low'
      }

      return {
        name: 'SMS Service',
        status,
        message,
        value: `${credits} credits`,
        threshold: '> 200 credits',
        percentage: Math.min((credits / 1000) * 100, 100),
        lastChecked: new Date().toISOString(),
        icon: MessageSquare,
      }
    } catch (error) {
      return {
        name: 'SMS Service',
        status: 'unknown',
        message: 'Unable to check credits',
        value: 'Unknown',
        lastChecked: new Date().toISOString(),
        icon: MessageSquare,
      }
    }
  }

  async function checkStorageUsage(): Promise<HealthMetric> {
    try {
      // Query total storage used across all churches
      const { data, error } = await supabase
        .from('churches')
        .select('storage_used_mb')

      if (error) throw error

      const totalUsedMB = (data || []).reduce((sum, church: any) => sum + (church.storage_used_mb || 0), 0)
      const totalUsedGB = totalUsedMB / 1024

      // Assuming 100GB Supabase plan
      const totalLimitGB = 100
      const percentage = (totalUsedGB / totalLimitGB) * 100

      let status: 'healthy' | 'warning' | 'error' = 'healthy'
      let message = 'Storage usage normal'

      if (percentage > 90) {
        status = 'error'
        message = 'Critical: Storage almost full!'
      } else if (percentage > 75) {
        status = 'warning'
        message = 'Warning: Storage usage high'
      }

      return {
        name: 'Storage',
        status,
        message,
        value: `${totalUsedGB.toFixed(2)} GB / ${totalLimitGB} GB`,
        percentage: Math.round(percentage),
        lastChecked: new Date().toISOString(),
        icon: HardDrive,
      }
    } catch (error) {
      return {
        name: 'Storage',
        status: 'unknown',
        message: 'Unable to check storage',
        value: 'Unknown',
        lastChecked: new Date().toISOString(),
        icon: HardDrive,
      }
    }
  }

  async function checkEdgeFunctions(): Promise<HealthMetric> {
    try {
      // Test a simple edge function
      const startTime = Date.now()
      const { data, error } = await supabase.functions.invoke('health-check', {
        body: { ping: true }
      })
      const responseTime = Date.now() - startTime

      // If health-check function doesn't exist, that's okay
      // Just check if edge functions are reachable
      const status = responseTime < 2000 ? 'healthy' : 'warning'
      const message = responseTime < 2000 ? 'Functions operational' : 'Slow response'

      return {
        name: 'Edge Functions',
        status,
        message,
        value: `${responseTime}ms`,
        threshold: '< 2s',
        lastChecked: new Date().toISOString(),
        icon: Zap,
      }
    } catch (error: any) {
      // If it's a 404 (function not found), that's actually okay - functions are reachable
      if (error.message?.includes('404') || error.message?.includes('FunctionsRelayError')) {
        return {
          name: 'Edge Functions',
          status: 'healthy',
          message: 'Functions reachable (no health-check function)',
          value: 'Operational',
          lastChecked: new Date().toISOString(),
          icon: Zap,
        }
      }

      return {
        name: 'Edge Functions',
        status: 'error',
        message: 'Functions unreachable',
        value: 'Offline',
        lastChecked: new Date().toISOString(),
        icon: Zap,
      }
    }
  }

  async function checkAPIResponseTime(): Promise<HealthMetric> {
    try {
      const startTime = Date.now()
      const { data, error } = await supabase
        .from('churches')
        .select('count')
        .limit(1)
      const responseTime = Date.now() - startTime

      if (error) throw error

      const status = responseTime < 200 ? 'healthy' : responseTime < 500 ? 'warning' : 'error'
      const message = responseTime < 200 ? 'API responding fast' : responseTime < 500 ? 'API slow' : 'API very slow'

      return {
        name: 'API Performance',
        status,
        message,
        value: `${responseTime}ms`,
        threshold: '< 200ms optimal',
        lastChecked: new Date().toISOString(),
        icon: Activity,
      }
    } catch (error) {
      return {
        name: 'API Performance',
        status: 'error',
        message: 'API unreachable',
        value: 'Offline',
        lastChecked: new Date().toISOString(),
        icon: Activity,
      }
    }
  }

  async function checkActiveUsers(): Promise<HealthMetric> {
    try {
      // Count churches with recent activity (logged in within last 24 hours)
      const oneDayAgo = new Date()
      oneDayAgo.setDate(oneDayAgo.getDate() - 1)

      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .gte('last_login_at', oneDayAgo.toISOString())

      if (error) throw error

      const activeCount = data?.length || 0

      return {
        name: 'Active Users',
        status: 'healthy',
        message: 'Users online in last 24h',
        value: `${activeCount} users`,
        lastChecked: new Date().toISOString(),
        icon: TrendingUp,
      }
    } catch (error) {
      return {
        name: 'Active Users',
        status: 'unknown',
        message: 'Unable to check active users',
        value: 'Unknown',
        lastChecked: new Date().toISOString(),
        icon: TrendingUp,
      }
    }
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case 'healthy':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Healthy</Badge>
      case 'warning':
        return <Badge className="bg-yellow-500"><AlertTriangle className="h-3 w-3 mr-1" />Warning</Badge>
      case 'error':
        return <Badge className="bg-red-500"><AlertCircle className="h-3 w-3 mr-1" />Error</Badge>
      default:
        return <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />Unknown</Badge>
    }
  }

  function getStatusColor(status: string) {
    switch (status) {
      case 'healthy': return 'text-green-500'
      case 'warning': return 'text-yellow-500'
      case 'error': return 'text-red-500'
      default: return 'text-gray-500'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Checking system health...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Activity className="h-8 w-8" />
            System Health Monitor
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time monitoring of platform infrastructure
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-sm text-muted-foreground">
            <div>Last checked:</div>
            <div className="font-medium">{lastRefresh.toLocaleTimeString()}</div>
          </div>
          <Button
            onClick={checkSystemHealth}
            disabled={refreshing}
            variant="outline"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Status Card */}
      <Card className={
        overallStatus === 'healthy' ? 'border-green-500' :
        overallStatus === 'warning' ? 'border-yellow-500' :
        'border-red-500'
      }>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            {overallStatus === 'healthy' && <CheckCircle className="h-6 w-6 text-green-500" />}
            {overallStatus === 'warning' && <AlertTriangle className="h-6 w-6 text-yellow-500" />}
            {overallStatus === 'error' && <AlertCircle className="h-6 w-6 text-red-500" />}
            Overall System Status
          </CardTitle>
          <CardDescription>
            {overallStatus === 'healthy' && 'All systems operational'}
            {overallStatus === 'warning' && 'Some systems require attention'}
            {overallStatus === 'error' && 'Critical issues detected - immediate action required'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {getStatusBadge(overallStatus)}
        </CardContent>
      </Card>

      {/* Health Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {healthMetrics.map((metric, index) => {
          const Icon = metric.icon
          return (
            <Card key={index} className={
              metric.status === 'error' ? 'border-red-200' :
              metric.status === 'warning' ? 'border-yellow-200' :
              ''
            }>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${getStatusColor(metric.status)}`} />
                    <CardTitle className="text-base">{metric.name}</CardTitle>
                  </div>
                  {getStatusBadge(metric.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">{metric.message}</p>
                  <p className="text-2xl font-bold mt-1">{metric.value}</p>
                  {metric.threshold && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Target: {metric.threshold}
                    </p>
                  )}
                </div>

                {metric.percentage !== undefined && (
                  <div className="space-y-1">
                    <Progress
                      value={metric.percentage}
                      className={
                        metric.percentage > 90 ? 'bg-red-200' :
                        metric.percentage > 75 ? 'bg-yellow-200' :
                        ''
                      }
                    />
                    <p className="text-xs text-muted-foreground text-right">
                      {metric.percentage}% used
                    </p>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Checked: {new Date(metric.lastChecked).toLocaleTimeString()}
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Alerts Section */}
      {healthMetrics.some(m => m.status === 'error' || m.status === 'warning') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Active Alerts
            </CardTitle>
            <CardDescription>
              Issues requiring attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {healthMetrics
                .filter(m => m.status === 'error' || m.status === 'warning')
                .map((metric, index) => {
                  const Icon = metric.icon
                  return (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg ${
                        metric.status === 'error' ? 'bg-red-50 dark:bg-red-950' : 'bg-yellow-50 dark:bg-yellow-950'
                      }`}
                    >
                      <Icon className={`h-5 w-5 mt-0.5 ${getStatusColor(metric.status)}`} />
                      <div className="flex-1">
                        <p className="font-medium">{metric.name}</p>
                        <p className="text-sm text-muted-foreground">{metric.message}</p>
                        <p className="text-sm font-mono mt-1">{metric.value}</p>
                      </div>
                      {getStatusBadge(metric.status)}
                    </div>
                  )
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common troubleshooting and management tasks</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate('/platform')}>
            <Server className="h-4 w-4 mr-2" />
            Platform Dashboard
          </Button>
          <Button variant="outline" onClick={() => navigate('/platform/subscriptions')}>
            <Database className="h-4 w-4 mr-2" />
            View Subscriptions
          </Button>
          <Button variant="outline" onClick={() => alert('SMS top-up page coming soon!')}>
            <MessageSquare className="h-4 w-4 mr-2" />
            Top Up SMS Credits
          </Button>
          <Button variant="outline" onClick={() => alert('Platform audit log coming soon!')}>
            <Activity className="h-4 w-4 mr-2" />
            View Audit Log
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
